import { IDs, PromptConstants } from "./../UiConstants";
import * as Configuration from "./../../Configuration/Configuration";
import {
	renderCVSurvey,
	ISurveyPayloadAdditionalInfo, EventType,
	QuestionType, ISurveyQuestionAnswer, IEventId,
} from "gatecrash";
import * as Api from "@ms-ofb/officefloodgatecore/dist/src/Api/Api";
import { Transporter } from "./../../Transport/Transport";
import * as Logging from "./../../Logging/Logging";
import { isNOU } from "@ms-ofb/officefloodgatecore/dist/src/Utils";
import { customerVoiceSurveyUX } from "../../Constants";
import { IOnDismissDelegate } from "../../IOnDismissDelegate";
import { IContextParams, ISurveyOptionalConfigParams } from "../../IRenderSurvey";
import { IHandleSurveyEvent, ISurveyEventData, SurveyEventKey } from "../../IHandleSurveyEvent";
import { IOverrideRenderCallback } from "../../IOverrideRenderCallback";

/**
 * Callback when the CV Survey dialog is dismissed
 */
let onDismiss: IOnDismissDelegate;
let onDismissFlag: boolean = false;
let isPromptResponseLogValidated: boolean = false;
let isAdditionalPromptResponseLogValidated: boolean = false;
let hasSubmittedData: boolean = false;
const SurveyUIFormSecondarySubmitName = "Survey_UI_Form_Secondary_Submit_Page_Number_";
const SurveyUIFormUserClosedName = "Survey_UI_Form_UserClosed_Page_Number_";
let firstCommentResponse: string = null;
let firstCommentTitle: string = "";

/**
 * Function to show Customer Voice survey experience
 */
export function createCVSurvey(
	contextParams: IContextParams,
	onClose: () => void,
	cvSurveyOptionalConfigParams?: ISurveyOptionalConfigParams,
	overrideUXRenderCallbackAsync?: IOverrideRenderCallback,
): void {
	onDismiss = function (submitted: boolean) {
		Configuration.get().getFloodgateInitOptions().onDismiss(
			Configuration.get().getFloodgateSurvey().getCampaignId(),
			submitted
		);
		onClose();
	};

	if (overrideUXRenderCallbackAsync) {
		const logCustomProps: Logging.ICustomProperties = {
			CampaignId: Configuration.get().getFloodgateSurvey().getCampaignId(),
			SurveyId: Configuration.get().getFloodgateSurvey().getId(),
			SurveyType: Configuration.get().getFloodgateSurvey().getSurveyType(),
		};

		overrideUXRenderCallbackAsync(contextParams)
			.then(() => {
				internal.logTelemetryEvent(Logging.EventIds.SURVEY_RENDER_OVERRIDE_SUCCESS, logCustomProps);
			}).catch((ex) => {
				logCustomProps.ErrorMessage = ex;
				internal.logTelemetryEvent(Logging.EventIds.SURVEY_RENDER_OVERRIDE_ERROR, logCustomProps);
			});
	} else {
		renderCVSurvey(
			IDs.ToastContainer,
			handleSurveyEvent,
			contextParams,
			cvSurveyOptionalConfigParams
		);
	}
}

/**
 * The default handler of events emitted by custom survey tools.
 * @param key an identifier for the event
 * @param data all data needed to process the event
 */
export const handleSurveyEvent: IHandleSurveyEvent = (
	key: SurveyEventKey,
	data: ISurveyEventData,
): void => {
	let keyPrefix: string;
	let keyBase: string;
	let err = false;

	const splitKey: string[] = key.split("_");
	if (splitKey.length > 1) {
		keyPrefix = splitKey[0];
		keyBase = splitKey[1];
	} else {
		keyPrefix = "Other";
		keyBase = splitKey[0];
	}

	switch (keyPrefix) {
		case "CV":
			switch (keyBase) {
				case "OnSubmit": return onCVSubmit(data);
				case "OnCancel": return onCVCancel(data);
				case "OnLoaded": return onCVLoaded(data);
				case "OnLog": return logGatecrashEvent(data);
				default: err = true;
			}
			break;
		default: err = true;
	}

	if (err) {
		const logCustomProps: Logging.ICustomProperties = {
			CampaignId: Configuration.get().getFloodgateSurvey().getCampaignId(),
			SurveyId: Configuration.get().getFloodgateSurvey().getId(),
			SurveyType: Configuration.get().getFloodgateSurvey().getSurveyType(),
		};
		internal.logTelemetryEvent(Logging.EventIds.SURVEY_EVENT_KEY_ERROR, logCustomProps);
	}
};

// Setting Rating field for SubmitData
export function getRating(values: ISurveyQuestionAnswer): number {
	return parseInt(values?.answerValue[0], 10) - 1;
}

// Setting QuestionValue field for SubmitData
export function getQuestionValue(values: ISurveyQuestionAnswer): string {
	return values?.questionValue;
}

// Setting AnswerValue field for SubmitData
export function getAnswerValue(values: ISurveyQuestionAnswer): string {
	return isNOU(values?.answerValue[0]) ? values?.answerValue[0] : `${values?.answerValue[0]}`;
}

// Setting InsightfulOptions for insightful field for SubmitData
export function getInsightfulOptions(values: ISurveyQuestionAnswer): string[] { // Evaluate
	return values?.insightfulQuestionOptions;
}

// Setting InsightfulAnswers for insightful field for SubmitData
export function getInsightfulAnswers(values: ISurveyQuestionAnswer): boolean[] { // Evaluate
	return values?.insightfulQuestionAnswers;
}

export interface IFormattedData {
	rating: number;
	commentsCollection?: string[];
	email?: string;
	ratingQuestion?: string;
	ratingsListLength: number;
	promptQuestion?: string;
	promptResponse?: string;
	commentTitlesCollection?: { question: string }[];
	additionalPromptQuestion?: string;
	additionalPromptResponse?: string;
	insightfulQuestionOptionsCollection?: { question: string, options: string[] }[];
	insightfulQuestionAnswersCollection?: boolean[][];
}

/* Traverses the CV Survey response object array and creates props for OCV Payload; 
 * on the face of it though it is O(N^2) runtime complexity, 
 * given the number of pages are only in finite number 
 * (wouldn't expect more than 10 pages at most), this should still run in constant time. 
 */
export function formatSubmitData(submitData: ISurveyEventData): IFormattedData {

	let rating: number;
	let commentsCollection: string[] = [];
	let email: string;
	let ratingQuestion: string;
	let ratingsListLength: number;
	let promptQuestion: string;
	let promptResponse: string;
	let commentTitlesCollection: { question: string }[] = [];
	let additionalPromptQuestion: string;
	let additionalPromptResponse: string;
	let insightfulQuestionOptionsCollection: { question: string, options: string[] }[] = [];
	let insightfulQuestionAnswersCollection: boolean[][] = [];
	let isFirstCommentValidated = false;
	let isFirstEmptyCommentAdded = false;

	submitData.pageInfo?.forEach(currentPage => {

		currentPage.questionAnswers.forEach(values => {

			switch (values?.questionType) {

				case QuestionType.Rating:

					rating = getRating(values);
					ratingQuestion = getQuestionValue(values);
					ratingsListLength = values.ratingsListLength;

					break;
				case QuestionType.Prompt:

					promptQuestion = getQuestionValue(values);
					promptResponse = getAnswerValue(values);

					break;

				case QuestionType.Verbatim:

					let commentResponse = getAnswerValue(values);
					let commentTitle = getQuestionValue(values);
					let cvSurveyHasPromptPage = !isNOU(promptResponse);
					let setCommentTitleAndValue: boolean = false;

					// This is a workaround because the OCV schema presents issues in the comments values section
					// when we send an array of null in the extended manifest data.
					// We will update this logic once the OCV team delivers the fix.
					if (isFirstCommentValidated) {
						if (
							!cvSurveyHasPromptPage &&
							submitData.pageNumber !== "1" &&
							isNOU(firstCommentResponse) &&
							!isFirstEmptyCommentAdded
						) {
							commentTitlesCollection.push({ question: firstCommentTitle });
							commentsCollection.push(" ");
							isFirstEmptyCommentAdded = true;
						}
						setCommentTitleAndValue = cvSurveyHasPromptPage || submitData.pageNumber !== "1";
					} else {
						firstCommentResponse = commentResponse;
						firstCommentTitle = commentTitle;
						isFirstCommentValidated = true;
						setCommentTitleAndValue = cvSurveyHasPromptPage || !isNOU(firstCommentResponse);
					}

					if (setCommentTitleAndValue) {
						commentTitlesCollection.push({ question: commentTitle });
						commentsCollection.push(isNOU(commentResponse) ? " " : commentResponse);
					}

					break;

				case QuestionType.Email:
					email = getAnswerValue(values);

					break;

				case QuestionType.AdditionalQuestion:

					additionalPromptQuestion = getQuestionValue(values);
					additionalPromptResponse = getAnswerValue(values);

					break;

				case QuestionType.ContactMe:
				default:

					insightfulQuestionOptionsCollection.push(
						{
							question: (values?.questionType === QuestionType.ContactMe) ? QuestionType.ContactMe : getQuestionValue(values),
							options: getInsightfulOptions(values),
						}
					);

					insightfulQuestionAnswersCollection.push(getInsightfulAnswers(values));

			}

		});

	});

	const formattedData: IFormattedData = {
		rating,
		ratingQuestion,
		ratingsListLength,
		email,
		commentsCollection,
		commentTitlesCollection,
		promptQuestion,
		promptResponse,
		additionalPromptQuestion,
		additionalPromptResponse,
		insightfulQuestionOptionsCollection,
		insightfulQuestionAnswersCollection,
	};

	return formattedData;
};

function updateExtendedManifestData(transporter: Transporter, formattedData: IFormattedData): string {
	let commentValues: string[];
	let commentTitles: { question: string }[] = [];

	if (formattedData.commentsCollection.length > 0) {
		commentValues = formattedData.commentsCollection;
		commentTitles = formattedData.commentTitlesCollection;
	}

	/* Titles */
	const extendedManifestData = JSON.parse(transporter.getExtendedManifestData()) as any;
	extendedManifestData.surveyStrings = JSON.stringify({
		prompt: { title: "", question: formattedData?.promptQuestion, yesButtonText: "YES", noButtonText: "NO" },
		rating: [{ question: formattedData?.ratingQuestion, options: [] }],
		comment: commentTitles,
		multipleChoice: formattedData.insightfulQuestionOptionsCollection,
	});
	/* Values */
	extendedManifestData.surveySpecificData = JSON.stringify({
		rating: [{ numberOfOptions: formattedData.ratingsListLength, selectedRating: formattedData.rating }],
		comment: commentValues,
		multipleChoice: formattedData.insightfulQuestionAnswersCollection,
	});
	transporter.setExtendedManifestData(JSON.stringify(extendedManifestData));
	return extendedManifestData;
}

function updateAppData(): string {
	let appData = Configuration.get().getCommonInitOptions().applicationGroup.appData;
	let appDataValues: {}[] = [];

	// To differentiate CV survey responses from other surveys given the flight object in OCV gets trimmed, this is a workaround for now.
	const cvSurveyInfo: ISurveyPayloadAdditionalInfo = {
		additionalInfo: {
			SurveyDescription: customerVoiceSurveyUX,
			Flight: Configuration.get().getCVSurveyFlights(),
		},
	};

	if (isNOU(appData)) {
		appDataValues.push({});
	} else {
		try {
			appDataValues.push(JSON.parse(appData));
		} catch (ex) {
			logTelemetryEvent(Logging.EventIds.APPDATA_JSON_PARSE_ERROR);
			appDataValues.push({});
		}
	}

	appDataValues.push(cvSurveyInfo);
	return JSON.stringify(appDataValues);
}

function setTransporterValues(transporter: Transporter, formattedData: IFormattedData): Transporter {
	transporter.setManifestValues(Configuration.get().getFloodgateSurvey().getJsonElements());
	updateExtendedManifestData(transporter, formattedData);

	if (formattedData.email) {
		transporter.setEmail(formattedData.email);
	}
	const appData = updateAppData();
	transporter.setAppData(appData);

	const preGeneratedId = Configuration.get().getFloodgateSurvey().getPreGeneratedClientFeedbackId();
	if (preGeneratedId) {
		transporter.setClientFeedbackId(preGeneratedId);
	}

	return transporter;
}

/**
 * Call-back function when user sends feedback from Customer Voice survey experience.
 * 
 * IMPORTANT: This callback should contain no references to any survey UI
 * nor DOM elements. This restriction supports multi-window environments
 * where rendering is happening in a different context than these callbacks.
 *
 * View the design document explaining multi-window support changes here:
 * https://microsoft-my.sharepoint.com/:w:/p/joshdunning/EWghU_fNcrpAseOemGqOO2QBBNaRz_wZDyEL0ZewuXwKtw?e=gi7P1a
 */
function onCVSubmit(data: ISurveyEventData): void {
	try {

		const formattedData = formatSubmitData(data);
		const { rating, promptResponse, additionalPromptResponse } = formattedData;

		const isPromptAnswerLogMandatory: boolean = validateLogPromptAnswer(promptResponse, additionalPromptResponse);
		// onCVSubmit is called twice when the user clicks NotNow an ICM was created for the CV team to avoid this behavior
		if (isPromptAnswerLogMandatory) {
			return;
		}

		// clientfeedback id to overwrite
		let transporter: Transporter = new Transporter(
			Configuration.get().getCommonInitOptions().environment,
			Api.ISurvey.Type[Configuration.get().getFloodgateSurvey().getSurveyType()],
			Configuration.get().getCommonInitOptions().appId,
			"Survey",
			Configuration.get().getCommonInitOptions().applicationGroup,
			Configuration.get().getCommonInitOptions().telemetryGroup,
			Configuration.get().getCommonInitOptions().webGroup,
		);

		let comment = isNOU(firstCommentResponse) ? null : firstCommentResponse;
		Configuration.get().getFloodgateSurvey().setValues(Number(rating), comment ? comment : null);
		setTransporterValues(transporter, formattedData);
		transporter.submit();

		if (
			data.eventType === EventType.FormPartialResponseSubmitted ||
			data.eventType === EventType.FormResponseSubmitted
		) {
			// Toggle OnDismissFlag when data is submitted to OCV
			onDismissFlag = true;
			if (hasSubmittedData) {
				Logging.EventIds.SURVEY_UI_FORM_SECONDARY_SUBMIT.name =
					SurveyUIFormSecondarySubmitName + data.pageNumber;
				logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_SECONDARY_SUBMIT);
			} else {
				logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_SUBMIT);
				hasSubmittedData = true;
			}
		}

	} catch (ex) {
		logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_SUBMIT_ERROR);
	}
}

function validateLogPromptAnswer(promptResponse: string, additionalPromptResponse: string): boolean {
	let isPromptAnswerLogNeeded: boolean = false;
	let isPromptAnswerNo: boolean = false;
	if (!isNOU(promptResponse)) {
		isPromptAnswerLogNeeded = LogPromptAnswer(promptResponse, QuestionType.Prompt, isPromptResponseLogValidated);
		isPromptAnswerNo = promptResponse === PromptConstants.No;
	}

	if (!isNOU(additionalPromptResponse)) {
		isPromptAnswerLogNeeded = LogPromptAnswer(additionalPromptResponse,
			QuestionType.AdditionalQuestion, isAdditionalPromptResponseLogValidated);
		isPromptAnswerNo = additionalPromptResponse === PromptConstants.No;
	}

	return isPromptAnswerLogNeeded || isPromptAnswerNo;
}

function LogPromptAnswer(promptResponse: string, questionType: string, isPromptAnswerLogged: boolean): boolean {

	if (!isPromptAnswerLogged) {

		let promptMessageClicked;
		let promptMessageClosed;
		if (questionType === QuestionType.Prompt) {
			isPromptResponseLogValidated = true;
			promptMessageClicked = Logging.EventIds.SURVEY_UI_PROMPT_CLICKED;
			promptMessageClosed = Logging.EventIds.SURVEY_UI_PROMPT_USERCLOSED;
			if ((promptResponse === PromptConstants.Yes)) {
				logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_SHOWN);
			}
		} else {
			isAdditionalPromptResponseLogValidated = true;
			promptMessageClicked = Logging.EventIds.SURVEY_UI_ADDITIONALPROMPT_CLICKED;
			promptMessageClosed = Logging.EventIds.SURVEY_UI_ADDITIONALPROMPT_USERCLOSED;
		}

		logTelemetryEvent(promptResponse === PromptConstants.No ? promptMessageClosed : promptMessageClicked);

		return true;
	}
	return false;
}

/**
 * Call-back function when user cancels feedback from Customer Voice survey experience.
 * 
 * IMPORTANT: This callback should contain no references to any survey UI
 * nor DOM elements. This restriction supports multi-window environments
 * where rendering is happening in a different context than these callbacks.
 *
 * View the design document explaining multi-window support changes here:
 * https://microsoft-my.sharepoint.com/:w:/p/joshdunning/EWghU_fNcrpAseOemGqOO2QBBNaRz_wZDyEL0ZewuXwKtw?e=gi7P1a
 */
function onCVCancel(data: ISurveyEventData): void {
	switch (data.eventType) {
		case EventType.FormClosed:
			if (isNOU(data.pageNumber)) {
				logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_USERCLOSED);
			} else {
				Logging.EventIds.SURVEY_UI_FORM_USERCLOSED.name = SurveyUIFormUserClosedName + data.pageNumber.charAt(data.pageNumber.length - 1);
				logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_USERCLOSED);
			}
			break;
		case EventType.ThankYouPageClosed:
			logTelemetryEvent(Logging.EventIds.SURVEY_UI_THANKYOUPAGE_USERCLOSED);
			break;
		case EventType.PromptClosed:
			logTelemetryEvent(Logging.EventIds.SURVEY_UI_PROMPT_USERCLOSED);
			break;
		case EventType.AdditionalPromptClosed:
			logTelemetryEvent(Logging.EventIds.SURVEY_UI_ADDITIONALPROMPT_USERCLOSED);
			break;
		case EventType.FormAutoDismissed:
			logTelemetryEvent(Logging.EventIds.SURVEY_UI_PROMPT_AUTODISMISSED);
			break;
		case EventType.ThankYouPageAutoDismissed:
			logTelemetryEvent(Logging.EventIds.SURVEY_UI_THANKYOUPAGE_AUTODISMISSED);
			break;
		case EventType.FormResponseCanceled:
			if (isNOU(data.pageNumber)) {
				logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_USERCLOSED);
			} else {
				Logging.EventIds.SURVEY_UI_FORM_USERCLOSED.name = SurveyUIFormUserClosedName + data.pageNumber?.charAt(data.pageNumber.length - 1);
				logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_USERCLOSED);
			}
			break;
		default:
			logTelemetryEvent(Logging.EventIds.SURVEY_UI_FORM_NO_RETURN_TYPE);
			break;
	}

	onDismiss(onDismissFlag);
}

/**
 * Call-back function when user Customer Voice survey is loaded and ready for showing to the user.
 * Here we are going to hide the survey if the current survey is the dispatcher survey.
 * First if the dispacther survey is called, the hasSurveyDispatcher parameter will be true.
 * And second, dispatcher survey will be the first survey in the survey dispatcher logic. So, we using 
 * hasLoadedDispatcher to make sure we will only hide the first survey in the whole survey dispatcher logic
 * which is the dispatcher survey.
 * 
 * IMPORTANT: This callback should contain no references to any survey UI
 * nor DOM elements. This restriction supports multi-window environments
 * where rendering is happening in a different context than these callbacks.
 *
 * View the design document explaining multi-window support changes here:
 * https://microsoft-my.sharepoint.com/:w:/p/joshdunning/EWghU_fNcrpAseOemGqOO2QBBNaRz_wZDyEL0ZewuXwKtw?e=gi7P1a
 */
function onCVLoaded(data: ISurveyEventData): void {
	let eventId: IEventId = Logging.EventIds.SURVEY_UI_FORM_SHOWN;

	if (data.hasSurveyDispatcher) {
		eventId = Logging.EventIds.SURVEY_UI_FORM_DISPATCHER_IMPLEMENTED;
	} else if (data.hasSurveyPromptShown) {
		eventId = Logging.EventIds.SURVEY_UI_PROMPT_SHOWN;
	}

	logTelemetryEvent(eventId);
}

/**
 * Call-back logging function for gatecrash errors.
 * 
 * IMPORTANT: This callback should contain no references to any survey UI
 * nor DOM elements. This restriction supports multi-window environments
 * where rendering is happening in a different context than these callbacks.
 *
 * View the design document explaining multi-window support changes here:
 * https://microsoft-my.sharepoint.com/:w:/p/joshdunning/EWghU_fNcrpAseOemGqOO2QBBNaRz_wZDyEL0ZewuXwKtw?e=gi7P1a
 */
function logGatecrashEvent(data: ISurveyEventData): void {
	const configuration = Configuration.get();
	const logCustomProps: Logging.ICustomProperties = {
		CampaignId: configuration.getFloodgateSurvey().getCampaignId(),
		SurveyId: configuration.getFloodgateSurvey().getId(),
		SurveyType: configuration.getFloodgateSurvey().getSurveyType(),
		ErrorMessage: data.customProperties?.ErrorMessage ? data.customProperties?.ErrorMessage : null,
		Message: data.customProperties?.Message ? data.customProperties?.Message : null,
	};

	logTelemetryEvent(data.eventId, logCustomProps);
}

// Log Telemetry for Customer Voice survey funnel.
function logTelemetryEvent(eventId: Logging.IEventId, customProperties?: Logging.ICustomProperties): void {
	const configuration = Configuration.get();
	let logCustomProps: Logging.ICustomProperties;
	if (!customProperties) {
		logCustomProps = {
			CampaignId: configuration.getFloodgateSurvey().getCampaignId(),
			SurveyId: configuration.getFloodgateSurvey().getId(),
			SurveyType: configuration.getFloodgateSurvey().getSurveyType(),
			ClientFeedbackId: Configuration.get().getFloodgateSurvey().getPreGeneratedClientFeedbackId(),
			CVFormId: configuration.getFloodgateSurvey().getCVSurveyFormId(),
		};
	} else {
		logCustomProps = customProperties;
	}

	Logging.getLogger().logEvent(
		eventId,
		Logging.LogLevel.Critical,
		logCustomProps
	);
}

const internal = {
	logTelemetryEvent,
};

export const { internal: unitTestExports } = { internal };
